import * as z from "zod";
import {
    CONTACT_RELATION,
    GENDER,
} from "../../../../prisma/generated/prisma/enums";
import { emailSchema, phoneSchema } from "@/shared/lib/validations";

export const userSchema = z.object({
    id: z.cuid(),
    email: emailSchema().max(255, "Email must be at most 255 characters"),
    image: z.any(),

    firstName: z
        .string({ error: "Please enter a valid first name" })
        .min(2, "First name must be at least 2 characters")
        .max(100, "First name must be at most 100 characters"),
    lastName: z
        .string({ error: "Please enter a valid last name" })
        .min(2, "Last name must be at least 2 characters")
        .max(100, "Last name must be at most 100 characters"),
    phone: phoneSchema().max(15, "Phone number is too long"),
    gender: z.enum(GENDER, { error: "Invalid gender" }),
    dob: z
        .date({ error: "Invalid date of birth" })
        .refine((d) => {
            const age = new Date().getFullYear() - d.getFullYear();
            return age >= 13 && age <= 100;
        }, "Member must be between 13 and 100 years old"),
    emergencyName: z
        .string({ error: "Please enter a valid emergency name" })
        .min(2, "Emergency contact name is required")
        .max(100, "Emergency name must be at most 100 characters"),
    emergencyPhone: phoneSchema(
        "Please enter a valid emergency phone number",
    ).max(15, "Emergency phone number is too long"),
    relationship: z.enum(CONTACT_RELATION, { error: "Invalid relationship" }),
    address: z
        .string({ error: "Please enter a valid address" })
        .min(2, "Please enter a member address")
        .max(255, "Address must be at most 255 characters"),
    startDate: z.coerce
        .date<Date>({ error: "Invalid date received" })
        .refine(
            (d) => d >= new Date(new Date().setHours(0, 0, 0, 0)),
            "Start date must be today or later",
        )
        .default(new Date()),
    password: z
        .string({ error: "Invalid password" })
        .min(8, "Password must be at least 8 characters")
        .max(50, "Password must be at most 50 characters"),
});
export const loginSchema = userSchema.pick({
    email: true,
    password: true,
});
export const registerSchema = userSchema.pick({
    email: true,
    firstName: true,
    lastName: true,
    phone: true,
    password: true,
});

export const forgotPasswordSchema = userSchema.pick({ email: true });

export const resetPasswordSchema = z
    .object({
        password: userSchema.shape.password,
        confirmPassword: userSchema.shape.password,
    })
    .refine((d) => d.password === d.confirmPassword, {
        error: "Passwords do not match",
        path: ["confirmPassword"],
    });

export type UserSchema = z.infer<typeof userSchema>;
export type LoginSchema = z.infer<typeof loginSchema>;
export type RegisterSchema = z.infer<typeof registerSchema>;
export type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;
